// src/utils/providerOutputValueUtils.js
// -----------------------------------------------------------------------------
// Generic provider output value helpers for Display Table / Submissions Table.
//
// Enterprise boundary:
//  • This file is provider-agnostic.
//  • It reads values from configurable paths supplied by provider adapters.
//  • It does NOT call provider APIs.
//  • It does NOT know Bitly/HubSpot/etc. schemas by name.
// -----------------------------------------------------------------------------

import {
  PROVIDER_COLUMN_ROLES,
  getProviderColumnSpec,
  normalizeProviderId,
} from './providerOutputColumnUtils';

export const PROVIDER_OUTPUT_STATUS = Object.freeze({
  IDLE: 'idle',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
  PENDING: 'pending',
});

const PENDING_STATUSES = new Set([
  PROVIDER_OUTPUT_STATUS.QUEUED,
  PROVIDER_OUTPUT_STATUS.PROCESSING,
  PROVIDER_OUTPUT_STATUS.PENDING,
]);

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizePathArray(paths) {
  if (!Array.isArray(paths)) return [];

  return paths
    .map((path) => normalizeString(path))
    .filter(Boolean);
}

function isUsableValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

export function getValueAtPath(obj, path = '') {
  const normalizedPath = normalizeString(path);

  if (!normalizedPath) return undefined;

  const parts = normalizedPath.split('.').filter(Boolean);
  let curr = obj;

  for (const part of parts) {
    if (!curr || typeof curr !== 'object') return undefined;
    curr = curr[part];
  }

  return curr;
}

export function getFirstUsableValueAtPaths(obj, paths = []) {
  const normalizedPaths = normalizePathArray(paths);

  for (const path of normalizedPaths) {
    const value = getValueAtPath(obj, path);

    if (isUsableValue(value)) {
      return value;
    }
  }

  return null;
}

export function coerceProviderDisplayValue(value) {
  if (value === null || value === undefined) return '';

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().toLocaleString();
    } catch (_) {
      return '';
    }
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => coerceProviderDisplayValue(item))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    if ('value' in value) {
      return coerceProviderDisplayValue(value.value);
    }

    return '';
  }

  return String(value);
}

function mergeProviderColumnConfig(column = {}, definition = null) {
  const fallback = definition || {};
  const merged = {
    ...fallback,
    ...column,
  };

  const valuePaths = normalizePathArray(column.valuePaths).length
    ? normalizePathArray(column.valuePaths)
    : normalizePathArray(fallback.valuePaths);

  const statusPaths = normalizePathArray(column.statusPaths).length
    ? normalizePathArray(column.statusPaths)
    : normalizePathArray(fallback.statusPaths);

  const errorPaths = normalizePathArray(column.errorPaths).length
    ? normalizePathArray(column.errorPaths)
    : normalizePathArray(fallback.errorPaths);

  const pendingPaths = normalizePathArray(column.pendingPaths).length
    ? normalizePathArray(column.pendingPaths)
    : normalizePathArray(fallback.pendingPaths);

  return {
    ...merged,
    valuePaths,
    statusPaths,
    errorPaths,
    pendingPaths,
    pendingText:
      column.pendingText ||
      fallback.pendingText ||
      'Pending...',
    errorText:
      column.errorText ||
      fallback.errorText ||
      'Unavailable',
  };
}

function readPendingState(row = {}, config = {}) {
  const pendingPaths = normalizePathArray(config.pendingPaths);

  for (const path of pendingPaths) {
    const value = getValueAtPath(row, path);

    if (value === true) return true;

    if (typeof value === 'string') {
      const normalized = value.toLowerCase();
      if (PENDING_STATUSES.has(normalized)) return true;
    }

    if (value && typeof value === 'object') {
      if (value.status === true) return true;

      const normalized = normalizeString(value.status).toLowerCase();
      if (PENDING_STATUSES.has(normalized)) return true;
    }
  }

  return false;
}

function getDefaultValuePaths(config = {}) {
  const provider = normalizeProviderId(config.provider);
  const role = normalizeString(config.role);
  const outputKey = normalizeString(config.outputKey);
  const metricKey = normalizeString(config.metricKey);

  if (!provider) return [];

  if (role === PROVIDER_COLUMN_ROLES.METRIC && metricKey) {
    return [
      `integrations.${provider}.metrics.${metricKey}`,
      `providerOutputs.${provider}.metrics.${metricKey}`,
    ];
  }

  if (role === PROVIDER_COLUMN_ROLES.OUTPUT && outputKey) {
    return [
      `integrations.${provider}.${outputKey}`,
      `providerOutputs.${provider}.${outputKey}`,
    ];
  }

  return [];
}

function getDefaultStatusPaths(config = {}) {
  const provider = normalizeProviderId(config.provider);
  if (!provider) return [];

  return [
    `integrations.${provider}.status`,
    `providerOutputs.${provider}.status`,
  ];
}

function getDefaultErrorPaths(config = {}) {
  const provider = normalizeProviderId(config.provider);
  if (!provider) return [];

  return [
    `integrations.${provider}.error`,
    `providerOutputs.${provider}.error`,
  ];
}

export function isProviderOutputPending({
  row,
  config,
  status,
}) {
  const normalizedStatus = normalizeString(status).toLowerCase();

  return (
    readPendingState(row, config) ||
    PENDING_STATUSES.has(normalizedStatus)
  );
}

export function isProviderOutputError(status) {
  return normalizeString(status).toLowerCase() === PROVIDER_OUTPUT_STATUS.ERROR;
}

/**
 * Resolve a provider-owned table cell.
 *
 * @param {object} row - submission row
 * @param {object} column - persisted column object
 * @param {object|null} definition - optional provider adapter column definition
 */
export function getProviderColumnDisplayState(
  row = {},
  column = {},
  definition = null
) {
  const spec = getProviderColumnSpec(column);
  const config = mergeProviderColumnConfig(
    {
      ...column,
      ...spec,
    },
    definition
  );

  if (!spec.isProviderColumn) {
    return {
      provider: '',
      key: '',
      value: null,
      displayText: '',
      status: null,
      error: null,
      isPending: false,
      isError: false,
      canCopy: false,
      raw: null,
    };
  }

  const valuePaths = config.valuePaths.length
    ? config.valuePaths
    : getDefaultValuePaths(config);

  const statusPaths = config.statusPaths.length
    ? config.statusPaths
    : getDefaultStatusPaths(config);

  const errorPaths = config.errorPaths.length
    ? config.errorPaths
    : getDefaultErrorPaths(config);

  const value = getFirstUsableValueAtPaths(row, valuePaths);
  const status = getFirstUsableValueAtPaths(row, statusPaths);
  const error = getFirstUsableValueAtPaths(row, errorPaths);

  const isPending = isProviderOutputPending({
    row,
    config,
    status,
  });

  const isError = isProviderOutputError(status);

  let displayText = coerceProviderDisplayValue(value);

  if (!displayText && isError) {
    displayText =
      config.outputKey === 'error' || config.metricKey === 'error'
        ? coerceProviderDisplayValue(error) || config.errorText
        : config.errorText;
  }

  if (!displayText && isPending) {
    displayText = config.pendingText;
  }

  const key =
    spec.role === PROVIDER_COLUMN_ROLES.METRIC
      ? spec.metricKey
      : spec.outputKey;

  return {
    provider: spec.provider,
    key,
    role: spec.role,
    value,
    displayText,
    status: status || (isPending ? PROVIDER_OUTPUT_STATUS.PENDING : null),
    error,
    isPending,
    isError,
    canCopy: Boolean(value),
    raw: value,
    config,
  };
}

export function getProviderColumnExportValue(
  row = {},
  column = {},
  definition = null
) {
  const state = getProviderColumnDisplayState(row, column, definition);

  if (isUsableValue(state.value)) {
    return coerceProviderDisplayValue(state.value);
  }

  return state.displayText || '';
}

export function getProviderColumnSearchTokens(
  row = {},
  columns = [],
  getDefinition = null
) {
  return (Array.isArray(columns) ? columns : [])
    .filter((column) => getProviderColumnSpec(column).isProviderColumn)
    .map((column) => {
      const definition =
        typeof getDefinition === 'function'
          ? getDefinition(column)
          : null;

      return getProviderColumnExportValue(row, column, definition);
    })
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
}